import { HelpScreenshot } from '../components/dialogs/HelpScreenshot';
import { helpContent, HelpContentKey, resolveHelpContentKey } from '../constants/helpContent';

// CRA can be deployed under a subpath (PUBLIC_URL). If we use absolute paths like `/help-screenshots/...`,
// assets will 404 when the app is hosted at `/some/subpath`. Prefixing with PUBLIC_URL makes these work
// both locally (PUBLIC_URL="") and in production.
// In dev (`npm start`), CRA serves from `/` and `PUBLIC_URL` can be set in the shell for other tooling,
// which would break asset URLs. Only respect PUBLIC_URL in production builds.
const _publicBase =
  (process.env.NODE_ENV === 'production' ? (process.env.PUBLIC_URL || '') : '').replace(/\/$/, '');
const _asset = (path: string) =>
  encodeURI(`${_publicBase}${path.startsWith('/') ? path : `/${path}`}`);

type HelpScreenshotFolder =
  | 'document-management'
  | 'category-management'
  | 'content-editor'
  | 'search'
  | 'ai-features';

const helpGuide = (
  id: string,
  title: string,
  helpKey: HelpContentKey,
  folder: HelpScreenshotFolder,
  assetBase: string,
  altText: string,
  options?: { animatedExt?: string; skipAnimated?: boolean },
): HelpScreenshot => {
  const entry = helpContent[helpKey];
  const animatedExt = options?.animatedExt ?? 'gif';
  const base = `/help-screenshots/${folder}/${assetBase}`;
  const screenshot: HelpScreenshot = {
    id,
    title,
    description: entry.detailed?.trim() || entry.simple,
    imagePath: _asset(`${base}.png`),
    altText,
    steps: entry.steps ?? [],
  };
  if (!options?.skipAnimated) {
    screenshot.animatedImagePath = _asset(`${base}-animated.${animatedExt}`);
  }
  return screenshot;
};

const documentGuide = (
  id: string,
  title: string,
  helpKey: HelpContentKey,
  assetBase: string,
  altText: string,
  options?: { animatedExt?: string },
): HelpScreenshot =>
  helpGuide(id, title, helpKey, 'document-management', assetBase, altText, options);

// Sample screenshot data - in a real implementation, this would come from the backend
const helpScreenshots: HelpScreenshot[] = [
  documentGuide(
    'load-document',
    'Import .nbr archive',
    'load-from-file',
    'load-document',
    'Screenshot showing Import .nbr archive in the document panel with file selection dialog open',
  ),
  documentGuide(
    'create-document',
    'Creating a New Document',
    'new-document',
    'create-document',
    'Screenshot showing the Create Document dialog with form fields for document name',
  ),
  documentGuide(
    'open-document',
    'Opening a Document',
    'open-document',
    'open-document',
    'Screenshot showing how to select a document and open it from the Document Menu',
  ),
  documentGuide(
    'close-document',
    'Closing a Document',
    'close-document',
    'close-document',
    'Screenshot showing how to close an open document using the Document Menu',
  ),
  documentGuide(
    'delete-document',
    'Deleting a Document',
    'delete-document',
    'delete-document',
    'Screenshot showing how to delete a document using the Document Menu',
  ),
  documentGuide(
    'rename-document',
    'Renaming a Document',
    'rename-document',
    'rename-document',
    'Screenshot showing how to rename a document using the Document Menu',
  ),
  helpGuide(
    'duplicate-as-document',
    'Duplicate as document',
    'duplicate-as-document',
    'category-management',
    'duplicate-as-document',
    'Screenshot showing the Duplicate as document dialog',
  ),
  documentGuide(
    'duplicate-document',
    'Duplicating a Document',
    'duplicate-document',
    'duplicate-document',
    'Screenshot showing how to duplicate a document using the Document Menu',
  ),
  documentGuide(
    'share-document',
    'Share with user',
    'share-document',
    'share-document',
    'Screenshot showing the Share document dialog',
  ),
  documentGuide(
    'suggest-tree-from-files',
    'New document from files (AI)',
    'suggest-tree',
    'ai-new-document',
    'Screenshot showing how to create a new document from files with AI',
  ),
  documentGuide(
    'expand-document-with-files',
    'Add to document from files (AI)',
    'expand-document',
    'ai-add-to-document',
    'Screenshot showing how to add content from files to a document with AI',
  ),
  helpGuide(
    'tree-assistant-input',
    'Structure builder: Instructions (AI)',
    'tree-assistant-input',
    'ai-features',
    'tree-assistant-instructions',
    'Screenshot showing the Structure builder Instructions (AI) tab',
  ),
  helpGuide(
    'tree-assistant-outline',
    'Structure builder: Import tab',
    'tree-assistant-outline',
    'ai-features',
    'tree-assistant-import',
    'Screenshot showing the Structure builder Import tab',
  ),
  documentGuide(
    'merge-document',
    'Merging Documents',
    'merge-document',
    'merge-document',
    'Screenshot showing how to merge documents using the Document Menu',
  ),
  documentGuide(
    'compare-documents',
    'Compare Documents',
    'document-merge-compare-menu',
    'compare-documents',
    'Screenshot showing how to compare documents side by side',
  ),
  helpGuide(
    'add-category',
    'Adding Categories to Your Document',
    'add-category',
    'category-management',
    'add-category',
    'Screenshot showing the Add Category dialog with form fields for category name and description',
  ),
  helpGuide(
    'rename-category',
    'Renaming a Category',
    'rename-category',
    'category-management',
    'rename-category',
    'Screenshot showing how to rename a category',
  ),
  helpGuide(
    'delete-category',
    'Deleting a Category',
    'delete-category',
    'category-management',
    'delete-category',
    'Screenshot showing how to delete a category',
  ),
  helpGuide(
    'group-categories',
    'Group selected subcategories',
    'group-categories',
    'category-management',
    'group-categories',
    'Screenshot showing the Group selected subcategories dialog',
  ),
  helpGuide(
    'move-category',
    'Moving Categories',
    'move-category',
    'category-management',
    'move-category',
    'Screenshot showing how to move categories',
  ),
  helpGuide(
    'drag-drop-categories',
    'Drag and drop categories',
    'drag-drop-categories',
    'category-management',
    'drag-drop-categories',
    'Screenshot showing how to drag and drop categories',
  ),
  helpGuide(
    'copy-paste-category',
    'Cutting, copying, and pasting categories',
    'cut-copy-paste-categories',
    'category-management',
    'copy-paste-categories',
    'Screenshot showing how to cut, copy, and paste categories',
  ),
  helpGuide(
    'import-subtree-from-document',
    'Import branch from document',
    'import-subtree-from-document',
    'category-management',
    'import-branch-from-document',
    'Screenshot showing the Import branch from document dialog',
  ),
  helpGuide(
    'merge-categories',
    'Merge selected content categories',
    'merge-categories',
    'category-management',
    'merge-categories',
    'Screenshot showing the Merge selected content categories dialog',
  ),
  helpGuide(
    'flatten-category',
    'Flatten category',
    'flatten-category',
    'category-management',
    'flatten-category',
    'Screenshot showing how to flatten a category',
  ),
  helpGuide(
    'collapse-subtree-to-content-category',
    'Collect branch content',
    'collapse-subtree-to-content-category',
    'category-management',
    'collect-branch-content',
    'Screenshot showing Collect branch content',
  ),
  helpGuide(
    'split-category',
    'Split into two subcategories',
    'split-category',
    'category-management',
    'split-category',
    'Screenshot showing the Split into two subcategories dialog',
  ),
  helpGuide(
    'compare-content',
    'Compare content and select',
    'compare-content',
    'content-editor',
    'compare-content',
    'Screenshot showing the Compare content and select dialog',
  ),
  helpGuide(
    'add-content',
    'Adding Content',
    'add-content',
    'content-editor',
    'add-content',
    'Screenshot showing how to add content',
  ),
  helpGuide(
    'show-sub-tree',
    'Category Structure Overview',
    'show-sub-tree',
    'category-management',
    'show-sub-tree',
    'Screenshot showing Category Structure Overview overlay',
  ),
  helpGuide(
    'select-subcase',
    'Select subcase',
    'select-subcase',
    'content-editor',
    'select-subcase',
    'Screenshot showing the Select Subcase dropdown in the content panel header',
  ),
  helpGuide(
    'rename-subcase',
    'Rename subcase',
    'rename-subcase',
    'content-editor',
    'rename-subcase',
    'Screenshot showing the Rename subcase dialog',
  ),
  helpGuide(
    'manage-subcase-groups',
    'Manage subcase groups',
    'manage-subcase-groups',
    'content-editor',
    'manage-subcase-groups',
    'Screenshot showing the Manage subcase groups dialog',
  ),
  helpGuide(
    'filter-content-pane-subcase',
    'Filter content by subcase',
    'content-pane-subcase-filter',
    'content-editor',
    'filter-content-pane-subcase',
    'Screenshot showing Filter content by subcase',
  ),
  helpGuide(
    'edit-content',
    'Editing Content in Categories',
    'edit-content',
    'content-editor',
    'edit-content',
    'Screenshot showing the content editor with rich text formatting toolbar and editing area',
  ),
  helpGuide(
    'delete-content',
    'Deleting Content',
    'delete-content',
    'content-editor',
    'delete-content',
    'Screenshot showing how to delete content from the content pane',
  ),
  helpGuide(
    'move-content',
    'Moving Content',
    'move-content',
    'content-editor',
    'move-content',
    'Screenshot showing how to move content rows within a content category',
  ),
  helpGuide(
    'drag-drop-content',
    'Drag and drop content',
    'drag-drop-content',
    'content-editor',
    'drag-drop-content',
    'Screenshot showing how to drag and drop content rows',
  ),
  helpGuide(
    'cut-copy-paste-content',
    'Cutting, copying, and pasting content',
    'cut-copy-paste-content',
    'content-editor',
    'cut-copy-paste-content',
    'Screenshot showing how to cut, copy, and paste content rows',
  ),
];

/** Help content keys whose screenshot id differs from the key itself. */
const helpKeyToScreenshotId: Partial<Record<HelpContentKey, string>> = {
  'new-document': 'create-document',
  'cut-copy-paste-categories': 'copy-paste-category',
  'load-from-file': 'load-document',
  'suggest-tree': 'suggest-tree-from-files',
  'expand-document': 'expand-document-with-files',
  'content-pane-subcase-filter': 'filter-content-pane-subcase',
};

export class HelpScreenshotService {
  static getScreenshotsForHelpKey(helpKey: HelpContentKey): HelpScreenshot[] {
    const id = helpKeyToScreenshotId[helpKey] ?? helpKey;
    const screenshot = helpScreenshots.find((s) => s.id === id);
    return screenshot ? [screenshot] : [];
  }

  /** Topic/keyword match, then structured help key from the question. */
  static getScreenshotsForQuestion(question: string): HelpScreenshot[] {
    const helpKey = resolveHelpContentKey(question);
    if (helpKey) {
      return this.getScreenshotsForHelpKey(helpKey);
    }
    return this.getScreenshotsForTopic(question);
  }

    static getScreenshotsForTopic(topic: string): HelpScreenshot[] {
    // Normalize user-visible topics (sidebar strings, tooltips, free text) so simple `includes`
    // matching remains reliable even with:
    // - non‑breaking spaces
    // - multiple whitespace runs
    // - zero-width chars
    // - minor punctuation differences
    const normalize = (s: string) =>
      (s || '')
        .toLowerCase()
        .replace(/[\u200B-\u200D\uFEFF]/g, '')
        .replace(/\u00A0/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    const lowerTopic = normalize(topic);
    
    // Map topics to screenshot IDs
    const topicToScreenshots: { [key: string]: string[] } = {
                  // Document Management
                  'load document': ['load-document'],
                  'loading documents': ['load-document'],
                  'load file': ['load-document'],
                  'nbr file': ['load-document'],
                  'import document': ['load-document'],
                  'open file': ['load-document'],
                  'how do i load': ['load-document'],
                  'how to load': ['load-document'],
                  'loading a document': ['load-document'],
                  'load from file': ['load-document'],
                  'load from nbr': ['load-document'],
                  'import from file': ['load-document'],
                  'import nbr archive': ['load-document'],
                  'import .nbr': ['load-document'],
                  'import .nbr archive': ['load-document'],
                  'open nbr file': ['load-document'],
                  'export to word': ['show-sub-tree'],
                  'export docx': ['show-sub-tree'],
                  
                  'create document': ['create-document'],
                  'new document': ['create-document'],
                  'how to create': ['create-document'],
                  'creating documents': ['create-document'],
                  'creating a document': ['create-document'],
                  'how do i create': ['create-document'],
                  'how do i make': ['create-document'],
                  'how to make': ['create-document'],
                  'how to start': ['create-document'],
                  'making a document': ['create-document'],
                  'start new document': ['create-document'],

                  'open document': ['open-document'],
                  'opening documents': ['open-document'],
                  'opening a document': ['open-document'],
                  'open selected document': ['open-document'],
                  'document menu open': ['open-document'],
                  'how to open': ['open-document'],
                  'how do i open': ['open-document'],
                  // Variations that may come from UI/tooltips
                  'open document:': ['open-document'],
                  'open document -': ['open-document'],
                  'open document (': ['open-document'],
                  'open document select': ['open-document'],
                  
                  'close document': ['close-document'],
                  'closing documents': ['close-document'],
                  'closing a document': ['close-document'],
                  'close selected document': ['close-document'],
                  'how to close': ['close-document'],
                  'how do i close': ['close-document'],
                  'close document:': ['close-document'],
                  'close document -': ['close-document'],
                  'close document (': ['close-document'],
                  
                  'delete document': ['delete-document'],
                  'deleting documents': ['delete-document'],
                  'deleting a document': ['delete-document'],
                  'delete selected document': ['delete-document'],
                  'how to delete': ['delete-document'],
                  'how do i delete': ['delete-document'],
                  'delete document:': ['delete-document'],
                  'delete document -': ['delete-document'],
                  'delete document (': ['delete-document'],

                  'rename document': ['rename-document'],
                  'renaming documents': ['rename-document'],
                  'renaming a document': ['rename-document'],
                  'rename selected document': ['rename-document'],
                  'how to rename': ['rename-document'],
                  'how do i rename': ['rename-document'],
                  'rename document:': ['rename-document'],
                  'rename document -': ['rename-document'],
                  'rename document (': ['rename-document'],

                  'duplicate document': ['duplicate-document'],
                  'duplicating documents': ['duplicate-document'],
                  'duplicating a document': ['duplicate-document'],
                  'duplicate selected document': ['duplicate-document'],
                  'how to duplicate': ['duplicate-document'],
                  'how do i duplicate': ['duplicate-document'],
                  'duplicate document:': ['duplicate-document'],
                  'duplicate document -': ['duplicate-document'],
                  'duplicate document (': ['duplicate-document'],
                  'duplicate as document': ['duplicate-as-document'],
                  'duplicate category as document': ['duplicate-as-document'],
                  'subtree as new document': ['duplicate-as-document'],
                  'full-tree view': ['show-sub-tree'],
                  'full tree view': ['show-sub-tree'],
                  'category structure overview': ['show-sub-tree'],
                  'category tree overview': ['show-sub-tree'],
                  'branch view': ['show-sub-tree'],
                  'view branch': ['show-sub-tree'],
                  'select subcase': ['select-subcase'],
                  'selecting subcase': ['select-subcase'],
                  'how do i select a subcase?': ['select-subcase'],
                  'rename subcase': ['rename-subcase'],
                  'how do i rename a subcase?': ['rename-subcase'],
                  'manage subcase groups': ['manage-subcase-groups'],
                  'how do i manage subcase groups?': ['manage-subcase-groups'],
                  'share with user': ['share-document'],
                  'how do i share a document with another user?': ['share-document'],
                  'share document': ['share-document'],
                  'sharing with user': ['share-document'],
                  'import branch from document': ['import-subtree-from-document'],
                  'collect branch content': ['collapse-subtree-to-content-category'],
                  'collecting branch content': ['collapse-subtree-to-content-category'],
                  'how do i use full-tree view?': ['show-sub-tree'],
                  'how do i duplicate a category as a document?': ['duplicate-as-document'],
                  'new document from category': ['duplicate-as-document'],

                  'suggest tree from files': ['suggest-tree-from-files'],
                  'suggesting trees from files': ['suggest-tree-from-files'],
                  'new document from files': ['suggest-tree-from-files'],
                  'new document from files (ai)': ['suggest-tree-from-files'],
                  'suggest category structure from files': ['suggest-tree-from-files'],
                  'suggest category structure': ['suggest-tree-from-files'],
                  'suggest category tree from files': ['suggest-tree-from-files'],
                  'suggest category tree': ['suggest-tree-from-files'],
                  'suggest tree': ['suggest-tree-from-files'],
                  'ai suggest tree': ['suggest-tree-from-files'],
                  'ai suggested tree': ['suggest-tree-from-files'],
                  'ai suggest document': ['suggest-tree-from-files'],
                  'suggest document': ['suggest-tree-from-files'],
                  'upload files': ['suggest-tree-from-files'],
                  'upload file': ['suggest-tree-from-files'],
                  'import files': ['suggest-tree-from-files'],
                  'import file': ['suggest-tree-from-files'],
                  
                  'add to document from files': ['expand-document-with-files'],
                  'add to document from files (ai)': ['expand-document-with-files'],
                  'expand document with files': ['expand-document-with-files'],
                  'expanding documents with files': ['expand-document-with-files'],
                  'expand documents with files': ['expand-document-with-files'],
                  'expand document': ['expand-document-with-files'],
                  'expanding a document': ['expand-document-with-files'],
                  'expand selected document': ['expand-document-with-files'],
                  'ai expand document': ['expand-document-with-files'],
                  'ai expand': ['expand-document-with-files'],
                  'expand with files': ['expand-document-with-files'],
                  'expand from files': ['expand-document-with-files'],
                  'add files to document': ['expand-document-with-files'],
                  'append files to document': ['expand-document-with-files'],

                  'merge document': ['merge-document'],
                  'merging document': ['merge-document'],
                  'merging documents': ['merge-document'],
                  'merging a document': ['merge-document'],
                  'merge documents': ['merge-document'],
                  'merge selected document': ['merge-document'],
                  'how to merge': ['merge-document'],
                  'how do i merge': ['merge-document'],
                  'combine documents': ['merge-document'],
                  'combine document': ['merge-document'],
                  'merge compare': ['compare-documents'],
                  'document merge compare': ['compare-documents'],
                  'compare documents': ['compare-documents'],
                  'compare two documents': ['compare-documents'],
                  'compare with': ['compare-documents'],
                  'side by side compare': ['compare-documents'],
                  'compare..': ['compare-documents'],
                  'compare with…': ['compare-documents'],

                  // Category Management
                  'how do i create sub categories': ['add-category'],
                  'how do i create subcategories': ['add-category'],
                  'how to create sub categories': ['add-category'],
                  'how to create subcategories': ['add-category'],
                  'create sub categories': ['add-category'],
                  'create subcategories': ['add-category'],
                  'add sub categories': ['add-category'],
                  'add subcategories': ['add-category'],
                  'making subcategories': ['add-category'],
                  'child categories': ['add-category'],
                  'add category': ['add-category'],
                  'create category': ['add-category'],
                  'new category': ['add-category'],
                  'how to add category': ['add-category'],
                  'adding categories': ['add-category'],
                  'organize content': ['add-category'],
                  'how do i add category': ['add-category'],
                  'how to create category': ['add-category'],
                  'how do i create category': ['add-category'],
                  'making categories': ['add-category'],
                  'rename category': ['rename-category'],
                  'rename categories': ['rename-category'],
                  'renaming a category': ['rename-category'],
                  'renaming categories': ['rename-category'],
                  'how to rename category': ['rename-category'],
                  'how do i rename category': ['rename-category'],
                  'delete category': ['delete-category'],
                  'delete categories': ['delete-category'],
                  'deleting a category': ['delete-category'],
                  'deleting categories': ['delete-category'],
                  'how to delete category': ['delete-category'],
                  'how do i delete category': ['delete-category'],
                  'group categories': ['group-categories'],
                  'grouping categories': ['group-categories'],
                  'group category': ['group-categories'],
                  'group-categories': ['group-categories'],
                  'how to group categories': ['group-categories'],
                  'how do i group categories': ['group-categories'],
                  'group selected subcategories': ['group-categories'],
                  'group subcategories': ['group-categories'],
                  'new parent category': ['group-categories'],
                  'put categories under parent': ['group-categories'],
                  'drag and drop categories': ['drag-drop-categories'],
                  'drag drop categories': ['drag-drop-categories'],
                  'drag and drop category': ['drag-drop-categories'],
                  'drag drop category': ['drag-drop-categories'],
                  'move category': ['move-category'],
                  'moving categories': ['move-category'],
                  'move categories': ['move-category'],
                  'reorder categories': ['move-category'],
                  'cutting, copying, and pasting categories': ['copy-paste-category'],
                  'cut copy paste categories': ['copy-paste-category'],
                  'cutting copying and pasting categories': ['copy-paste-category'],
                  'copying and pasting categories': ['copy-paste-category'],
                  'copy and paste categories': ['copy-paste-category'],
                  'cut categories': ['copy-paste-category'],
                  'cut category': ['copy-paste-category'],
                  'copy categories': ['copy-paste-category'],
                  'copy category': ['copy-paste-category'],
                  'paste categories': ['copy-paste-category'],
                  'paste category': ['copy-paste-category'],
                  'importing a subtree from another document': ['import-subtree-from-document'],
                  'import subtree from document': ['import-subtree-from-document'],
                  'import subtree': ['import-subtree-from-document'],
                  'how do i import a subtree from another document?': ['import-subtree-from-document'],
                  'copy subtree from document': ['import-subtree-from-document'],
                  'clone subtree': ['import-subtree-from-document'],
                  'copy branch from another document': ['import-subtree-from-document'],
                  'merge categories': ['merge-categories'],
                  'merging categories': ['merge-categories'],
                  'merge category': ['merge-categories'],
                  'merging a category': ['merge-categories'],
                  'how to merge categories': ['merge-categories'],
                  'how do i merge categories': ['merge-categories'],
                  'merge content categories': ['merge-categories'],
                  'merge selected content': ['merge-categories'],
                  'combine content categories': ['merge-categories'],
                  'flatten category': ['flatten-category'],
                  'flattening categories': ['flatten-category'],
                  'flatten categories': ['flatten-category'],
                  'flatten a category': ['flatten-category'],
                  'how to flatten': ['flatten-category'],
                  'how do i flatten': ['flatten-category'],
                  'collect subtree content': ['collapse-subtree-to-content-category'],
                  'collect subtree': ['collapse-subtree-to-content-category'],
                  'collapse subtree': ['collapse-subtree-to-content-category'],
                  'turn branch into content': ['collapse-subtree-to-content-category'],
                  'content category from branch': ['collapse-subtree-to-content-category'],
                  'gather subtree rows': ['collapse-subtree-to-content-category'],
                  'structure categories': ['structure-categories'],
                  'structure category': ['structure-categories'],
                  'structure categories ai': ['structure-categories'],
                  'generate initial categories': ['structure-categories', 'generate-initial-categories'],
                  'initial categories': ['structure-categories', 'generate-initial-categories'],
                  'bottom up categories': ['structure-categories'],
                  'bottom-up categories': ['structure-categories'],
                  'cluster rows into categories': ['structure-categories'],
                  'split into subcategories': ['structure-categories', 'generate-subtree'],
                  'split into subcategories ai': ['structure-categories', 'generate-subtree'],
                  'generate subtree': ['structure-categories', 'generate-subtree'],
                  'top down categories': ['structure-categories'],
                  'top-down subtree': ['structure-categories'],
                  'ai subtree': ['structure-categories'],
                  'add parent layers': ['structure-categories', 'suggest-parent-structure'],
                  'parent layers': ['structure-categories', 'suggest-parent-structure'],
                  'propose parent': ['structure-categories'],
                  'group subcategories ai': ['structure-categories'],
                  'bottom up parents': ['structure-categories'],
                  'split category': ['split-category'],
                  'splitting categories': ['split-category'],
                  'splitting a category': ['split-category'],
                  'how to split': ['split-category'],
                  'split a category': ['split-category'],
                  'split into two subcategories': ['split-category'],
                  'two subcategories': ['split-category'],
                  'add matching content': ['add-matching-content'],
                  'add matching content (ai)': ['add-matching-content'],
                  'matching content': ['add-matching-content'],
                  'copy matching rows': ['add-matching-content'],
                  'move matching rows': ['add-matching-content'],
                  'semantic matching content': ['add-matching-content'],
                  'add matching rows': ['add-matching-content'],
                  'matching threshold': ['add-matching-similarity-threshold'],
                  'compare content and select': ['compare-content'],
                  'compare content': ['compare-content'],
                  'how do i compare content and select?': ['compare-content'],
                  'how do i compare content and select': ['compare-content'],
                  'compare rows': ['compare-content'],
                  'select unmatched': ['compare-content'],
                  'select unmatched rows': ['compare-content'],
                  'select matched rows': ['compare-content'],
                  'temp cleanup': ['compare-content'],
                  'unmatched rows': ['compare-content'],
                  'compare to branch': ['compare-content'],
                  'compare to category': ['compare-content'],
                  
                  // Content Editing
                  'editing content': ['edit-content'],
                  'edit content': ['edit-content'],
                  'content editor': ['edit-content'],
                  'writing content': ['edit-content'],
                  'deleting content': ['delete-content'],
                  'delete content': ['delete-content'],
                  'moving content': ['move-content'],
                  'move content': ['move-content'],
                  'drag and drop content': ['drag-drop-content'],
                  'drag drop content': ['drag-drop-content'],
                  'cutting, copying, and pasting content': ['cut-copy-paste-content'],
                  'cut copy paste content': ['cut-copy-paste-content'],
                  'add content': ['add-content'],
                  'adding content': ['add-content'],
                  'how to add content': ['add-content'],
                  'add new content': ['add-content'],
                  'text editor': ['edit-content'],
                  'rich text': ['edit-content'],
                  'how to edit content': ['edit-content'],
                  'how do i edit content': ['edit-content'],
                  'how to write content': ['edit-content'],
                  'how do i write content': ['edit-content'],
                  'how do i add content': ['add-content'],

                  'filtering the content pane by subcase': ['filter-content-pane-subcase'],
                  'how do i filter the content pane by subcase?': ['filter-content-pane-subcase'],
                  'filter content pane by subcase': ['filter-content-pane-subcase'],
                  'filter content by subcase': ['filter-content-pane-subcase'],
                  'filter by subcase': ['filter-content-pane-subcase'],
                  'how do i filter content by subcase': ['filter-content-pane-subcase'],
                  'content pane subcase filter': ['filter-content-pane-subcase'],
                  'filter the content pane': ['filter-content-pane-subcase'],
                  'hide subcases in content pane': ['filter-content-pane-subcase'],
                  'show only subcase rows': ['filter-content-pane-subcase'],
                  'show sub tree': ['show-sub-tree'],
                  'show subtree': ['show-sub-tree'],
                  'sub tree view': ['show-sub-tree'],
                  'horizontal tree': ['show-sub-tree'],
                  'graphical tree': ['show-sub-tree'],
                  'graphical view': ['show-sub-tree'],
                  'textual view': ['show-sub-tree'],
                  'vertical tree view': ['show-sub-tree'],

                  // Search Features
                  'search content': ['search-content'],
                  'find content': ['search-content'],
                  'search feature': ['search-content'],
                  'how to search': ['search-content'],
                  'finding text': ['search-content'],
                  'how do i search': ['search-content'],
                  'how to find content': ['search-content'],
                  'how do i find content': ['search-content'],
                  'searching for text': ['search-content'],
                  
                  'ask ai': ['ask-ai'],
                  'ask-ai': ['ask-ai'],
                  'how do i use ask ai': ['ask-ai'],
                  'how to use ask ai': ['ask-ai'],
                  'ai assistant': ['ask-ai'],
                  'document q&a': ['ask-ai'],
                  'finding text in documents': ['search-content'],
                  'find: semantic mode': ['search-content'],
                  'semantic search': ['search-content'],
                  'semantic (find)': ['search-content'],
                  'how to use semantic search': ['search-content'],
                  'how do i use semantic search': ['search-content'],
                  'find semantic': ['search-content'],
                  'semantic find': ['search-content'],
                };

    // Find matching keywords in the topic, prioritizing longer matches
    const matchingScreenshotIds: string[] = [];
    
    // Sort keywords by length (longest first) to prioritize more specific matches
    const sortedKeywords = Object.entries(topicToScreenshots).sort((a, b) => b[0].length - a[0].length);
    
    for (const [keyword, screenshotIds] of sortedKeywords) {
      if (lowerTopic.includes(normalize(keyword))) {
        matchingScreenshotIds.push(...screenshotIds);
        // Break after first match to avoid cross-matching
        break;
      }
    }
    
    const screenshots = helpScreenshots.filter(screenshot => 
      matchingScreenshotIds.includes(screenshot.id)
    );
    
    return screenshots;
  }

  static getScreenshotById(id: string): HelpScreenshot | undefined {
    return helpScreenshots.find(screenshot => screenshot.id === id);
  }

  static getAllScreenshots(): HelpScreenshot[] {
    return helpScreenshots;
  }
} 